/*
  The game header's language toggle (docs/specs/pop-language-split.md D2, D9).

  It takes the place of the "Graded in" chips, in the same spot and the same pill shape
  (HeaderPill), so the header keeps its look and the row now does something. One button per
  bucket the game has, in English, Japanese, Chinese, Other order; with fewer than two
  there is nothing to choose and the row is not drawn at all (D2).

  A labelled group of plain buttons reporting aria-pressed, exactly as GameView's display
  mode buttons are: the active one stays enabled and in the tab order, and says it is on.
  The active colours are the mode buttons' too — goldText on the raised surface — which
  reads in navy, gilded and the event skin, dark and light, because every class is a
  themed token.
*/

import { CONTROL_LABEL, FOCUS_RING } from 'components/styles';
import { LANG_LABELS, LangBucket } from 'lib/setLanguage';

interface LanguageToggleProps {
  /* The buckets this game has, already in button order (bucketsPresent). */
  buckets: readonly LangBucket[];
  value: LangBucket;
  onChange: (bucket: LangBucket) => void;
}

const BUTTON_BASE =
  `rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${FOCUS_RING}`;

const buttonClass = (active: boolean): string =>
  `${BUTTON_BASE} ${
    active
      ? 'border-pcg-goldText bg-pcg-raised text-pcg-goldText'
      : 'border-pcg-border text-pcg-text hover:bg-pcg-raised hover:text-pcg-goldText'
  }`;

const LanguageToggle = ({ buckets, value, onChange }: LanguageToggleProps) => {
  if (buckets.length < 2) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {/* The group's aria-label already says it; this is the sighted reader's copy. */}
      <span aria-hidden="true" className={CONTROL_LABEL}>
        Language
      </span>
      <div role="group" aria-label="Language" className="flex flex-wrap items-center gap-2">
        {buckets.map(bucket => (
          <button
            key={bucket}
            type="button"
            aria-pressed={bucket === value}
            onClick={() => onChange(bucket)}
            className={buttonClass(bucket === value)}
          >
            {LANG_LABELS[bucket]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageToggle;
